/* pb-cart.js — Headless unified cart via Storefront API */

// ---------- Consent & Klaviyo helpers ----------
function canUserBeTracked() {
  try {
    return !!(window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.userCanBeTracked());
  } catch (e) { return false; }
}

// Wait for consent if not granted yet, with a timeout fallback
function waitForConsent(ms = 5000) {
  return new Promise((resolve) => {
    if (canUserBeTracked()) return resolve(true);

    let done = false;
    const onConsent = () => { if (!done) { done = true; resolve(true); } };

    // Shopify emits this after the banner choice (name can vary by app; this one is most common)
    document.addEventListener('visitorConsentCollected', onConsent, { once: true });

    setTimeout(() => { if (!done) resolve(false); }, ms);
  });
}

// Ensure Klaviyo queue exists and retry a few times for library readiness
function klaviyoQueueReady() {
  return !!(window._learnq && window._learnq._loaded === true && typeof window._learnq.push === 'function');
}


async function ensureKlaviyoReady(retries = 25, waitMs = 200) {
  // don't create _learnq here — let Klaviyo own it
  for (let i = 0; i < retries; i++) {
    if (klaviyoQueueReady()) return true;
    await new Promise(r => setTimeout(r, waitMs));
  }

  console.warn('[Klaviyo] _learnq not loaded — event queued only');
  // fallback: create queue so we don't crash
  window._learnq = window._learnq || [];
  return false;
}

// Identify helper: prefer quiz email, then Shopify customer if theme exposes it
function klaviyoIdentifyIfPossible() {
  try {
    window._learnq = window._learnq || [];

    const quizEmail = localStorage.getItem('quiz_email');
    const themeCustomerEmail =
      window?.Shopify?.customer?.email || window?.__PB_CUSTOMER_EMAIL__ || '';

    const $email = (quizEmail && quizEmail.trim()) ? quizEmail.trim()
                  : (themeCustomerEmail && String(themeCustomerEmail).trim())
                  ? String(themeCustomerEmail).trim()
                  : '';

    if ($email) {
      window._learnq.push(['identify', { $email }]);
      window.PBDebug?.log('[Klaviyo] identify', { $email });
      return true;
    }
  } catch (e) {
    console.warn('[Klaviyo] identify failed', e);
  }
  return false;
}

// Unified safe tracker that waits for consent + (best-effort) Klaviyo readiness
async function klaviyoTrack(eventName, props = {}) {
  try {
    // Wait for consent (flushes later if user accepts)
    const consentOk = canUserBeTracked() ? true : await waitForConsent(6000);
    if (!consentOk && !canUserBeTracked()) {
      console.warn('[Klaviyo] consent not granted, skipping event:', eventName);
      return false;
    }

    await ensureKlaviyoReady();
    klaviyoIdentifyIfPossible();
    
    window._learnq = window._learnq || [];
    window._learnq.push(['track', eventName, props]);
    window.PBDebug?.log('[Klaviyo] track →', eventName, props);
    return true;
  } catch (e) {
    console.warn('[Klaviyo] track failed', e);
    return false;
  }
}


(() => {
  // ---------- Config ----------
  const cfg = () => (window.PB_UNIFIED_CART || {});
  const ENDPOINT = () => cfg().endpoint;
  const TOKEN    = () => cfg().token;
  const COOKIE_DOMAIN = () => (cfg().cookieDomain || apex(location.hostname));
  const AUTO_HOOK = () => cfg().autoHookAddToCart === true;
  const CUSTOMER_EMAIL = () => String(cfg().customerEmail || '').trim().toLowerCase();

  // Wait for endpoint + token
  const waitForConfig = async () => {
    let tries = 0;
    while (!ENDPOINT() || !TOKEN()) {
      await new Promise(r => setTimeout(r, 30));
      if (++tries > 200) throw new Error('[PBCart] Missing endpoint/token config');
    }
  };

  // Apex helper
  function apex(host) {
    const parts = String(host || location.hostname).split('.');
    return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : `.${host}`;
  }

  // ---------- Cookie ----------
  const CART_COOKIE = 'pb_cart_id';

  function setCookie(name, val, days = 30) {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = `${name}=${encodeURIComponent(val)};expires=${d.toUTCString()};path=/;domain=${COOKIE_DOMAIN()};SameSite=None;Secure`;
  }

  function getCookie(name) {
    const hit = document.cookie.split('; ').find(r => r.startsWith(name + '='));
    return hit ? decodeURIComponent(hit.split('=')[1]) : null;
  }

  function delCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${COOKIE_DOMAIN()};SameSite=None;Secure`;
  }

  // ---------- GQL (with RETRY + THROTTLE handling) ----------
  // Every request is bounded: a Storefront call that neither answers nor fails
  // (a dropped mobile connection, a proxy that swallows the response) used to
  // leave the promise pending forever, and with it whatever was waiting on it
  // — the pre-sale checkout button stayed disabled with no way back. 15s is
  // generous for a cart mutation and short enough to hand the visitor a retry.
  const REQUEST_TIMEOUT_MS = 15000;

  async function fetchBounded(url, init) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : 0;
    try {
      return await fetch(url, controller ? { ...init, signal: controller.signal } : init);
    } catch (error) {
      if (controller && controller.signal.aborted) {
        const timeout = new Error(`[PBCart] Request timed out after ${REQUEST_TIMEOUT_MS}ms`);
        timeout.code = 'TIMEOUT';
        throw timeout;
      }
      throw error;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  async function gql(query, variables = {}) {
    await waitForConfig();

    let attempts = 0;

    while (attempts < 5) {
      const res = await fetchBounded(ENDPOINT(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": TOKEN()
        },
        body: JSON.stringify({ query, variables })
      });

      const json = await res.json();

      // SUCCESS
      if (json.data && !json.errors) {
        return json.data;
      }

      // THROTTLED → retry
      if (json.errors && json.errors[0]?.extensions?.code === "THROTTLED") {
        const waitMs = 300 * (attempts + 1);
        console.warn(`[PBCart][THROTTLED] waiting ${waitMs}ms…`);
        await new Promise(r => setTimeout(r, waitMs));
        attempts++;
        continue;
      }

      // OTHER ERRORS
      throw new Error(`[PBCart] GQL error: ${JSON.stringify(json.errors)}`);
    }

    throw new Error("[PBCart] Failed after max retries (THROTTLED)");
  }

  // ---------- Cart Fragment ----------
  const CART_FRAGMENT = `
  fragment CartMini on Cart {
    id
    checkoutUrl
    totalQuantity
    attributes { key value }

    buyerIdentity { email }

    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }

    discountCodes {
      applicable
      code
    }

    # Order-level codes (OLIVIA15, NATHALIE15…) are allocated to the cart, not to a line.
    discountAllocations {
      discountedAmount { amount }
      ... on CartCodeDiscountAllocation { code }
    }
    
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes { key value }

          # Een cart transform vervangt de kindregels door één parent-regel van
          # het type ComponentizableCartLine. Zonder deze selectie weet de drawer
          # niet meer wát er in die bundel zit.
          ... on ComponentizableCartLine {
            lineComponents {
              id
              quantity
              attributes { key value }
              cost {
                subtotalAmount { amount currencyCode }
                totalAmount { amount currencyCode }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  image { url altText }
                  product { id title handle }
                }
              }
            }
          }

          discountAllocations {
            discountedAmount { amount currencyCode }
          }

          cost {
            subtotalAmount { amount currencyCode }
            totalAmount { amount currencyCode }
          }

          merchandise {
            __typename
            ... on ProductVariant {
              id
              title
              sku
              image { url altText }
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              product {
                id title handle vendor
                cardTitle: metafield(namespace: "custom", key: "product_card_title") { value }
                subtitle: metafield(namespace: "custom", key: "product_subtitle") { value }
                ratingFor: metafield(namespace: "custom", key: "collection_page_rating_for") { value }
                cartNaming: metafield(namespace: "custom", key: "cart_naming") { value }
              }

              requiresComponents
              components(first: 30) {
                nodes {
                  quantity
                  productVariant {
                    id
                    title
                    sku
                    image { url altText }
                    product { id title handle vendor }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;


  // ---------- API OPS ----------
  async function cartCreate() {
    const d = await gql(`mutation { cartCreate(input:{}) { cart { ...CartMini } } } ${CART_FRAGMENT}`);
    return d.cartCreate.cart;
  }

  async function cartQuery(id) {
    const d = await gql(`query($id:ID!){ cart(id:$id){ ...CartMini } } ${CART_FRAGMENT}`, { id });
    return d.cart;
  }

  // Shopify clamps a cart line silently: ask for 42 of a variant that can only be
  // sold 38 times and the mutation succeeds with quantity 38 plus a warning. Without
  // selecting `warnings` the drawer never learns the requested quantity was refused.
  let _lastWarnings = [];

  function rememberWarnings(warnings) {
    _lastWarnings = Array.isArray(warnings) ? warnings : [];
    return _lastWarnings;
  }

  async function cartLinesAdd(cartId, lines) {
    const d = await gql(`
      mutation($cartId:ID!, $lines:[CartLineInput!]!) {
        cartLinesAdd(cartId:$cartId, lines:$lines) {
          cart { ...CartMini }
          userErrors { field message code }
          warnings { code message target }
        }
      }
      ${CART_FRAGMENT}
    `, { cartId, lines });

    // Without this, an unpublished / missing variant (Cart Sync) returns cart
    // unchanged or null, and callers still open an empty drawer.
    const errors = d.cartLinesAdd?.userErrors || [];
    if (errors.length) {
      const msg = errors.map((e) => e.message).join('; ');
      throw new Error(`[PBCart] cartLinesAdd refused: ${msg}`);
    }

    rememberWarnings(d.cartLinesAdd?.warnings);
    const cart = d.cartLinesAdd?.cart;
    if (!cart?.id) {
      throw new Error('[PBCart] cartLinesAdd returned no cart (check Cart Sync publication)');
    }
    return cart;
  }

  async function cartLinesUpdate(cartId, lines) {
    const d = await gql(`
      mutation($cartId:ID!,$lines:[CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId:$cartId, lines:$lines) {
          cart { ...CartMini }
          warnings { code message target }
        }
      }
      ${CART_FRAGMENT}
    `, { cartId, lines });
    rememberWarnings(d.cartLinesUpdate.warnings);
    return d.cartLinesUpdate.cart;
  }

  async function cartLinesRemove(cartId, lineIds) {
    const d = await gql(`
      mutation($cartId:ID!,$lineIds:[ID!]!) {
        cartLinesRemove(cartId:$cartId, lineIds:$lineIds) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, lineIds });
    return d.cartLinesRemove.cart;
  }

  async function cartBuyerIdentityUpdate(cartId, buyerIdentity) {
    const d = await gql(`
      mutation($cartId:ID!,$buyerIdentity:CartBuyerIdentityInput!) {
        cartBuyerIdentityUpdate(cartId:$cartId, buyerIdentity:$buyerIdentity) { cart { ...CartMini } }
      }
      ${CART_FRAGMENT}
    `, { cartId, buyerIdentity });
    return d.cartBuyerIdentityUpdate.cart;
  }

  async function cartAttributesUpdate(cartId, attributes) {
    const d = await gql(`
      mutation($cartId:ID!,$attributes:[AttributeInput!]!) {
        cartAttributesUpdate(cartId:$cartId, attributes:$attributes) {
          cart { ...CartMini }
          userErrors { field message }
        }
      }
      ${CART_FRAGMENT}
    `, { cartId, attributes });

    // Shopify weigert een attribuut stilletjes met cart null — zonder deze
    // check zou de cache op null komen te staan en de checkout daarop breken.
    const errors = d.cartAttributesUpdate?.userErrors || [];
    if (errors.length) console.warn('[PBCart] cart attributes refused', errors);

    return d.cartAttributesUpdate?.cart || null;
  }

  // Deze cart wordt via de Storefront API aangemaakt, dus de /discount/<code>
  // route die een Liquid-cart gebruikt raakt hem nooit: een kortingscode kan
  // alleen via deze mutatie op de cart komen. Let op dat de mutatie de HELE
  // lijst vervangt — elke caller moet dus ook de codes meesturen die moeten
  // blijven staan.
  async function cartDiscountCodesUpdate(cartId, discountCodes) {
    const d = await gql(`
      mutation($cartId:ID!,$discountCodes:[String!]!) {
        cartDiscountCodesUpdate(cartId:$cartId, discountCodes:$discountCodes) {
          cart { ...CartMini }
          userErrors { field message }
        }
      }
      ${CART_FRAGMENT}
    `, { cartId, discountCodes });

    const errors = d.cartDiscountCodesUpdate?.userErrors || [];
    if (errors.length) console.warn('[PBCart] discount codes refused', errors);

    return d.cartDiscountCodesUpdate.cart;
  }

  // ---------- Boot ----------
  let _cartId = null;
  let _cartPromise = null;

  // Eén lopende initialisatie voor iedereen. De boot hieronder start ensureCart
  // zonder te wachten, en window.PBCart bestaat al vóór die klaar is: een vroege
  // caller (landingspagina die een kortingscode zet, een snelle add-to-cart) zou
  // anders een tweede cartCreate starten. Het tragere antwoord overschrijft dan
  // _cartId en de cookie, en de cart waar de caller op schreef is weg.
  let _ensurePromise = null;

  function ensureCart() {
    if (_cartId) return Promise.resolve(_cartId);
    if (!_ensurePromise) {
      _ensurePromise = initCart().finally(() => { _ensurePromise = null; });
    }
    return _ensurePromise;
  }

  async function initCart() {
    await waitForConfig();

    if (_cartId) return _cartId;

    // try cookie
    const cookieId = getCookie(CART_COOKIE);
    if (cookieId) {
      try {
        const c = await cartQuery(cookieId);
        if (c?.id) {
          _cartId = c.id;
          _cartPromise = Promise.resolve(c);

          // Ook de teruggehaalde cart stempelen, niet alleen een verse. De
          // cart-cookie leeft 30 dagen, dus een terugkerende bezoeker kan met
          // een volle cart uit een vorige sessie rechtstreeks afrekenen zonder
          // nog iets te muteren — `current()` geeft dan deze gecachete cart
          // terug en er komt geen mutatie meer langs. Zonder deze regel mist
          // precies die order zijn huidige Dalton-toewijzing, en dat zijn geen
          // willekeurige orders: het zijn de terugkerende klanten.
          // ensureAttributionAttributes doet geen API-call als er niets te
          // veranderen valt, dus de gewone bezoeker betaalt er niets voor.
          const stamped = await ensureAttributionAttributes(c);
          await ensureBuyerIdentity(stamped);
          return _cartId;
        }
      } catch (err) {}
    }

    // create new cart
    let created = await cartCreate();

    // ensure first-touch attribute is set on cart
    created = await ensureAttributionAttributes(created);
    created = await ensureBuyerIdentity(created);

    _cartId = created.id;
    _cartPromise = Promise.resolve(created);
    setCookie(CART_COOKIE, _cartId);

    document.dispatchEvent(updatedEvent(created, "boot"));
    return _cartId;
  }

  function gidFromVariant(id) {
    const s = String(id);
    return s.startsWith("gid://")
      ? s
      : `gid://shopify/ProductVariant/${s.replace(/\D/g, "")}`;
  }

  // The cart's own id carries the token its checkout is addressed by:
  // gid://shopify/Cart/<token>?key=<key>. The host comes from the checkoutUrl
  // Shopify handed back, so the link stays on whatever domain the shop calls
  // its own instead of one hardcoded here. Anything unexpected in either value
  // falls back to the checkoutUrl: a cart permalink still reaches the checkout,
  // an empty string reads as a failed hand-off and would lose the tap.
  function directCheckoutUrl(cartId, checkoutUrl) {
    try {
      const token = String(cartId || "").split("/Cart/")[1]?.split("?")[0];
      if (!token) return checkoutUrl;
      return new URL(`/checkouts/cn/${token}`, checkoutUrl).toString();
    } catch (e) {
      return checkoutUrl;
    }
  }

  function updatedEvent(cart, source = "update", warnings = []) {
    return new CustomEvent("pb:cart:updated", {
      detail: { source, cart, warnings }
    });
  }

  // ---------- First-touch + Last-touch brand → Cart attributes ----------
  const FIRST_TOUCH_COOKIE = 'pb_first_touch_brand';
  const FIRST_TOUCH_KEY    = 'pb_first_touch_brand';

  const LAST_TOUCH_COOKIE  = 'pb_last_touch_brand';
  const LAST_TOUCH_KEY     = 'pb_last_touch_brand';

  const AFFILIATE_COOKIE = 'pb_affiliate_name';
  const AFFILIATE_KEY    = 'pb_affiliate_name';

  // ---------- Dalton A/B → cart attribute ----------
  // Eén versioned JSON-attribuut als enige bron van waarheid: in welk
  // experiment de bezoeker zat, welke arm hij zag, in welke Dalton-sessie dat
  // gebeurde en wanneer. Die vier worden samen vastgelegd en blijven samen —
  // los bijgehouden leverde orders op met de arm van vorige week en de sessie
  // van vandaag, omdat Dalton na 24 uur opnieuw indeelt.
  //
  //   {"version":1,"assignments":[
  //     {"experiment":"22272","arm":"75147","session":"abc","assignedAt":1757808000}]}
  //
  // Shopify bewaart de waarde als gewone string, dus JSON.stringify erin en
  // parsen bij de verwerking in Converge of de Railway-db.
  //
  // theme.liquid legt de toewijzingen vast in localStorage; hier worden ze
  // samengevoegd met wat al op de cart staat en teruggeschreven. De underscore
  // houdt het attribuut uit de checkout en de orderbevestiging van de klant.
  const DALTON_STORE   = 'pb_dalton_attribution';
  const DALTON_KEY     = '_dalton_attribution';
  const DALTON_VERSION = 1;

  // Gelijk aan MAX_AGE_SECONDS in theme.liquid. De filter draait ook over wat
  // al op de cart staat, zodat een verlopen toewijzing er weer af gaat in
  // plaats van mee te reizen tot de order.
  const DALTON_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
  const DALTON_MAX_ASSIGNMENTS = 20;

  const daltonNow = () => Math.floor(Date.now() / 1000);

  function isFreshAssignment(assignment, now) {
    if (!assignment || typeof assignment !== 'object') return false;
    if (!assignment.experiment || !assignment.arm) return false;

    const at = Number(assignment.assignedAt);
    return Number.isFinite(at) && now - at < DALTON_MAX_AGE_SECONDS;
  }

  // Vaste vorm, zodat de payload compact blijft en twee keer dezelfde
  // toewijzing ook echt dezelfde string oplevert — daar hangt de
  // "is er iets veranderd"-vergelijking hieronder op.
  function normalizeAssignment(assignment) {
    return {
      experiment: String(assignment.experiment),
      arm: String(assignment.arm),
      session: assignment.session ? String(assignment.session) : null,
      assignedAt: Number(assignment.assignedAt)
    };
  }

  // Defensief: dit is tracking. Kapotte JSON — een oudere versie, een
  // afgebroken write, iemand die het attribuut met de hand aanpaste — mag nooit
  // een add-to-cart of een checkout laten mislukken. Dan liever opnieuw
  // beginnen dan struikelen.
  function parseDaltonAttribution(raw) {
    const now = daltonNow();
    try {
      const parsed = JSON.parse(raw || '');
      if (!parsed || parsed.version !== DALTON_VERSION || !Array.isArray(parsed.assignments)) return [];

      return parsed.assignments.filter(a => isFreshAssignment(a, now)).map(normalizeAssignment);
    } catch (e) {
      return [];
    }
  }

  function getDaltonStore() {
    try {
      return parseDaltonAttribution(localStorage.getItem(DALTON_STORE));
    } catch (e) {
      return [];
    }
  }

  // First assignment wins per experiment, en op de hele toewijzing. Wat al op
  // de cart staat gaat voor: dat is de toewijzing waarmee de bezoeker deze cart
  // begon, dus een herbucketing halverwege laat de order niet alsnog van arm
  // wisselen en kan arm en sessie nooit uit verschillende sessies combineren.
  function mergeDaltonAssignments(...lists) {
    const byExperiment = new Map();

    lists.flat().forEach(assignment => {
      const key = String(assignment.experiment);
      if (!byExperiment.has(key)) byExperiment.set(key, assignment);
    });

    return [...byExperiment.values()].slice(-DALTON_MAX_ASSIGNMENTS);
  }

  // Lege lijst geeft een lege string, en die laat het attribuut weg in plaats
  // van er `{"assignments":[]}` op te zetten. Zo verdwijnt een cart-attribuut
  // waarvan alles verlopen is ook echt.
  function serializeDaltonAttribution(assignments) {
    if (!assignments.length) return '';
    return JSON.stringify({ version: DALTON_VERSION, assignments });
  }

  // ---------- Free gift A/B test → cart attribute ----------
  // The Auto Gifter unlock popup (app embed) keeps the visitor's arm in the
  // cookie pb_fg_test on .planetb.care for 30 days. The cart mirrors it, so
  // the order says which arm the visitor saw:
  //
  //   {"version":1,"experiment":"free-gift-email-gate-v1","arm":"email","assignedAt":1761040000}
  //
  // No cookie (expired, or removed by the app when the test stopped) means no
  // attribute. Like _dalton_attribution, Shopify stores it as a plain string.
  const FREE_GIFT_TEST_COOKIE = 'pb_fg_test';
  const FREE_GIFT_TEST_KEY    = '_pb_free_gift_test';

  // Defensive like the Dalton parser: a broken cookie means no attribute,
  // never a failed add to cart or checkout.
  function getFreeGiftTest() {
    try {
      const parsed = JSON.parse(getCookie(FREE_GIFT_TEST_COOKIE) || '');
      if (!parsed || parsed.version !== 1 || !parsed.experiment || !parsed.arm) return '';
      return JSON.stringify({
        version: 1,
        experiment: String(parsed.experiment),
        arm: String(parsed.arm),
        assignedAt: Number(parsed.assignedAt) || null
      });
    } catch {
      return '';
    }
  }

  function getFirstTouchBrand() {
    return getCookie(FIRST_TOUCH_COOKIE) || 'planetb';
  }

  // Make this idempotent so it never becomes last_last_*
  function getLastTouchValue() {
    const raw = getCookie(LAST_TOUCH_COOKIE) || 'planetb';
    const brand = String(raw || 'planetb').trim().toLowerCase();
    return brand.startsWith('last_') ? brand : `last_${brand}`;
  }

  function getAffiliateName() {
    return getCookie(AFFILIATE_COOKIE) || null;
  }

  // ---------- ensureAttributionAttributes ----------
  // Only fires a cartAttributesUpdate when the values are actually missing or outdated.
  // This prevents a race condition (CONFLICT error) caused by two simultaneous Storefront
  // API mutations on the same cart — which happened because addLine + ensureAttributionAttributes
  // were both mutating the cart in quick succession on every add.
  async function ensureAttributionAttributes(cart) {
    if (!cart?.id) return cart;

    const first     = getFirstTouchBrand();
    const last      = getLastTouchValue();
    const affiliate = getAffiliateName();

    const existingAttrs = cart.attributes || [];

    // Eén bezoeker kan experimenten zien op meerdere subdomeinen (wondr.,
    // powr., …) terwijl dezelfde cart ze allemaal moet dragen. localStorage is
    // per origin, dus samenvoegen met wat er al op de cart staat in plaats van
    // overschrijven — anders wist een add op het ene merk de test van het
    // andere.
    //
    // parseDaltonAttribution filtert daarbij ook de cart zelf: is er iets
    // verlopen, dan verschilt de nieuwe waarde van de oude en wordt de
    // opgeschoonde JSON teruggeschreven. Zo geldt de vervaltermijn niet alleen
    // voor localStorage maar ook voor wat er al op de cart stond.
    const onCart = existingAttrs.find(a => a.key === DALTON_KEY)?.value || '';
    const daltonAttribution = serializeDaltonAttribution(
      mergeDaltonAssignments(parseDaltonAttribution(onCart), getDaltonStore())
    );

    // Check if our attribution attributes are already correctly set
    const firstOk     = existingAttrs.some(a => a.key === FIRST_TOUCH_KEY && a.value === String(first));
    const lastOk      = existingAttrs.some(a => a.key === LAST_TOUCH_KEY  && a.value === String(last));
    const affiliateOk = !affiliate || existingAttrs.some(a => a.key === AFFILIATE_KEY && a.value === affiliate);
    const daltonOk    = daltonAttribution === onCart;

    const freeGiftTest   = getFreeGiftTest();
    const freeGiftOnCart = existingAttrs.find(a => a.key === FREE_GIFT_TEST_KEY)?.value || '';
    const freeGiftOk     = freeGiftTest === freeGiftOnCart;

    // Skip the API call entirely if nothing needs to change
    if (firstOk && lastOk && affiliateOk && daltonOk && freeGiftOk) return cart;

    // Preserve any existing attributes that aren't ours (e.g. Convert keys)
    const preserved = existingAttrs
      .filter(a => a.key !== FIRST_TOUCH_KEY && a.key !== LAST_TOUCH_KEY && a.key !== WONDR_PRIZE_ATTRIBUTE && a.key !== AFFILIATE_KEY
                && a.key !== DALTON_KEY && a.key !== FREE_GIFT_TEST_KEY)
      .map(a => ({ key: a.key, value: a.value }));

    const toSet = [
      ...preserved,
      { key: FIRST_TOUCH_KEY, value: String(first) },
      { key: LAST_TOUCH_KEY,  value: String(last)  },
      ...(affiliate      ? [{ key: AFFILIATE_KEY,       value: affiliate      }] : []),
      ...(daltonAttribution ? [{ key: DALTON_KEY, value: daltonAttribution }] : []),
      ...(freeGiftTest ? [{ key: FREE_GIFT_TEST_KEY, value: freeGiftTest }] : [])
    ];

    // Attributie mag de cart nooit in de weg zitten, en deze write kan wel
    // degelijk gooien: een timeout uit fetchBounded, een netwerkfout, een
    // GraphQL-error. Dat heeft twee nare uitwerkingen als je het laat lopen.
    // Op een uit de cookie teruggehaalde cart bubbelt het naar de try in
    // ensureCart(), die dan doorvalt naar cartCreate() en de winkelmand van de
    // bezoeker vervangt door een lege. En in addLine() is de regel al
    // toegevoegd voordat dit faalt, dus de UI meldt een fout en een retry legt
    // het product er een tweede keer in.
    //
    // Liever een order zonder attributie dan een kapotte cart, dus bij elke
    // fout de cart teruggeven zoals hij was.
    let updated = null;
    try {
      updated = await cartAttributesUpdate(cart.id, toSet);
    } catch (e) {
      console.warn('[PBCart] attribution write failed, keeping cart as-is', e);
      return cart;
    }

    // Geweigerd (userErrors, cart null): zelfde afweging, en zo komt er geen
    // lege promise in de cache waar de checkout straks op stukloopt.
    if (!updated) return cart;

    _cartPromise = Promise.resolve(updated);
    return updated;
  }


  // ---------- ensureBuyerIdentity ----------
  // A Storefront API cart has no idea who the shopper is: unlike the Liquid cart it
  // does not inherit the online-store login session. Automatic discounts that key off
  // the customer — Planet B Member Korting reads
  // buyerIdentity.customer.hasTags(["Planet_B_Member"]) — therefore never applied in
  // the drawer. They only kicked in once checkout attached the email to the cart,
  // which is why the discount appeared after visiting checkout and coming back.
  //
  // Writing the logged-in email to buyerIdentity is enough for Shopify to resolve the
  // customer server-side, so the discount function runs on the cart as well.
  const IDENTITY_KEY = 'pb_cart_identity_email';

  function readAppliedIdentity() {
    try { return localStorage.getItem(IDENTITY_KEY) || ''; } catch (e) { return ''; }
  }

  function writeAppliedIdentity(email) {
    try {
      if (email) localStorage.setItem(IDENTITY_KEY, email);
      else localStorage.removeItem(IDENTITY_KEY);
    } catch (e) {}
  }

  async function ensureBuyerIdentity(cart) {
    if (!cart?.id) return cart;

    const email = CUSTOMER_EMAIL();
    const onCart = String(cart.buyerIdentity?.email || '').trim().toLowerCase();

    let next;
    if (email) {
      if (onCart === email) { writeAppliedIdentity(email); return cart; }
      next = email;
    } else {
      // Logged out. Only drop an email we put there ourselves, so an email the
      // shopper typed in checkout survives a return to the storefront.
      if (!readAppliedIdentity() || !onCart) return cart;
      next = null;
    }

    try {
      const updated = await cartBuyerIdentityUpdate(cart.id, { email: next });
      writeAppliedIdentity(next);
      _cartPromise = Promise.resolve(updated);
      document.dispatchEvent(updatedEvent(updated, 'identity'));
      return updated;
    } catch (e) {
      console.warn('[PBCart] buyer identity sync failed', e);
      return cart;
    }
  }

  // ---------- PUBLIC API ----------
  const PBCart = {
    // Warnings returned by the most recent cartLinesAdd / cartLinesUpdate, e.g.
    // MERCHANDISE_NOT_ENOUGH_STOCK when Shopify clamped the requested quantity.
    get lastWarnings() {
      return _lastWarnings;
    },

    async refresh() {
      await ensureCart();
      try {
          let c = await cartQuery(_cartId);
          c = await ensureAttributionAttributes(c);
          c = await ensureBuyerIdentity(c);
          _cartPromise = Promise.resolve(c);
          document.dispatchEvent(updatedEvent(c, "refresh"));
          return c;
      } catch (e) {
          // ── NIEUW: retry één keer voordat je een nieuwe cart maakt ──
          try {
              await new Promise(r => setTimeout(r, 500));
              let c = await cartQuery(_cartId);
              c = await ensureAttributionAttributes(c);
              c = await ensureBuyerIdentity(c);
              _cartPromise = Promise.resolve(c);
              document.dispatchEvent(updatedEvent(c, "refresh"));
              return c;
          } catch (retryErr) {
              console.warn('[PBCart] refresh failed after retry, creating new cart', retryErr);
              let newCart = await cartCreate();
              newCart = await ensureAttributionAttributes(newCart);
              newCart = await ensureBuyerIdentity(newCart);
              _cartId = newCart.id;
              setCookie(CART_COOKIE, _cartId);
              _cartPromise = Promise.resolve(newCart);
              document.dispatchEvent(updatedEvent(newCart, "refresh:new"));
              return newCart;
          }
      }
  },

    async addLine(variantIdOrGid, qty = 1, attributes = [], sellingPlanId = null) {
      await ensureCart();

      const gid = gidFromVariant(variantIdOrGid);

      const sellingPlanGid = sellingPlanId
        ? (String(sellingPlanId).startsWith('gid://') 
            ? String(sellingPlanId) 
            : `gid://shopify/SellingPlan/${String(sellingPlanId).replace(/\D/g, '')}`)
        : null;
      
      const payload = {
        merchandiseId: gid,
        quantity: Math.max(1, Number(qty) || 1)
      };
      if (attributes?.length) payload.attributes = attributes;
      if (sellingPlanId) payload.sellingPlanId = sellingPlanGid;

      window.PBDebug?.log("=== PBCart.addLine CALLED ===", { variant: variantIdOrGid, qty });

      const c = await cartLinesAdd(_cartId, [payload]);
      const warnings = _lastWarnings;

      // ensureAttributionAttributes will only fire a cartAttributesUpdate if the
      // attribution values are missing or outdated — avoiding the CONFLICT error
      // that occurred when two mutations fired simultaneously on the same cart.
      const cart = await ensureAttributionAttributes(c);

      // Find the line that matches the variant we added (don't assume edges[0]).
      // A cart transform can group it under a parent line (lineComponents), so the
      // mutation succeeded if the variant is there at top level or as a component.
      const lines = (cart?.lines?.edges || []).map(e => e.node);
      const line = lines.find(n => n?.merchandise?.id === gid) ||
        lines.find(n => (n?.lineComponents || []).some(c => c?.merchandise?.id === gid));

      if (!line) {
        throw new Error(
          `[PBCart] addLine: variant not in cart after mutation (${gid}). ` +
            'Publish the product to Online Store and Cart Sync.'
        );
      }

      /*
      // --- Klaviyo tracking (safe wrapper) ---
      await klaviyoTrack('planetb_add_to_cart', {
        cart_id: cart?.id,
        variant_id: line?.merchandise?.id,
        product_id: line?.merchandise?.product?.id,
        product_handle: line?.merchandise?.product?.handle,
        product_name: line?.merchandise?.product?.title,
        variant_name: line?.merchandise?.title,
        sku: line?.merchandise?.sku || null,
        price: Number(line?.merchandise?.price?.amount || 0),
        currency: line?.merchandise?.price?.currencyCode,
        quantity: Number(line?.quantity || payload.quantity),
        ts: Date.now()
      });
      */

      document.dispatchEvent(new CustomEvent('cart:open', { detail: { source: 'after_add' } }));

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "add", warnings));
      return cart;
    },

    // Zet meerdere regels in één mutatie in de cart. Zelfde resultaat als
    // addLine per item, maar met één cartLinesAdd en één keer openen van de
    // drawer — voor quick-add secties die een vast setje producten tegelijk
    // toevoegen. Regels zonder eigen attributes voegt Shopify samen met een
    // bestaande identieke regel, zodat een automatische korting op de totale
    // hoeveelheid blijft rekenen.
    //
    // Items: { id | variantId, quantity, attributes?, sellingPlanId? }
    async addLines(items = []) {
      await ensureCart();

      const lines = (Array.isArray(items) ? items : [])
        .filter((item) => item && (item.id || item.variantId))
        .map((item) => {
          const payload = {
            merchandiseId: gidFromVariant(item.id || item.variantId),
            quantity: Math.max(1, Number(item.quantity) || 1)
          };

          if (item.attributes?.length) payload.attributes = item.attributes;

          if (item.sellingPlanId) {
            const raw = String(item.sellingPlanId);
            payload.sellingPlanId = raw.startsWith('gid://')
              ? raw
              : `gid://shopify/SellingPlan/${raw.replace(/\D/g, '')}`;
          }

          return payload;
        });

      if (!lines.length) {
        throw new Error('[PBCart] addLines called without variants');
      }

      window.PBDebug?.log("=== PBCart.addLines CALLED ===", lines);

      const c = await cartLinesAdd(_cartId, lines);
      const warnings = _lastWarnings;
      const cart = await ensureAttributionAttributes(c);

      const wanted = new Set(lines.map((l) => l.merchandiseId));
      // Top-level lines plus components of grouped lines (cart transforms)
      const have = new Set(
        (cart?.lines?.edges || [])
          .flatMap((e) => [
            e?.node?.merchandise?.id,
            ...(e?.node?.lineComponents || []).map((c) => c?.merchandise?.id)
          ])
          .filter(Boolean)
      );
      const missing = [...wanted].filter((id) => !have.has(id));
      if (missing.length) {
        throw new Error(
          `[PBCart] addLines: variant(s) not in cart after mutation (${missing.join(', ')}). ` +
            'Publish the product to Online Store and Cart Sync.'
        );
      }

      document.dispatchEvent(new CustomEvent('cart:open', { detail: { source: 'after_add' } }));

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "add", warnings));
      return cart;
    },

    async updateLine(lineId, qty) {
      await ensureCart();

      let cart = await cartLinesUpdate(_cartId, [{ id: lineId, quantity: qty }]);
      const warnings = _lastWarnings;

      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "update", warnings));
      return cart;
    },

    async replacePickMixBundle(payload = {}) {
      await ensureCart();

      window.PBDebug?.log('[PBCart] replacePickMixBundle called', payload);

      const cartItems = Array.isArray(payload.cartItems) ? payload.cartItems : [];

      if (!cartItems.length) {
        throw new Error('[PBCart] Pick & Mix payload has no cartItems');
      }

      const firstPickMixProps = cartItems[0]?.properties || {};

      const pickMixBundleId =
        firstPickMixProps._pick_mix_id ||
        firstPickMixProps.pb_pick_mix_id ||
        '';

      const pickMixEditUrl =
        payload.editUrl ||
        localStorage.getItem('pb_pick_mix_edit_url_latest') ||
        '/pages/pick-mix?pm_edit=1';

      function cleanVariantId(value) {
        const raw = String(value || '');
        const lastPart = raw.includes('/') ? raw.split('/').pop() : raw;
        return lastPart.replace(/\D/g, '') || raw;
      }

      function getPickMixImageUrl(item) {
        const img =
          item.image ||
          item.imageUrl ||
          item.featured_image ||
          item.featuredImage ||
          item.productImage ||
          item.media ||
          '';

        if (typeof img === 'string') return img;

        if (img?.url) return img.url;
        if (img?.src) return img.src;
        if (img?.image?.url) return img.image.url;
        if (img?.image?.src) return img.image.src;
        if (img?.preview_image?.src) return img.preview_image.src;
        if (img?.featured_image?.src) return img.featured_image.src;

        return '';
      }

      const displaySourceItems = Array.isArray(payload.basketItems) && payload.basketItems.length
        ? payload.basketItems
        : cartItems;

      const pickMixDisplayItems = displaySourceItems.map((item) => {
        const props = item.properties || {};
        const variantId = cleanVariantId(item.variantId || item.id);

        return {
          variantId,
          id: variantId,

          title:
            props._pick_mix_card_title ||
            props.pb_pick_mix_card_title ||
            item.title ||
            'Item',

          subtitle:
            props._pick_mix_subtitle ||
            props.pb_pick_mix_subtitle ||
            item.subtitle ||
            '',

          variantTitle:
            item.variantTitle ||
            props._pick_mix_variant_title ||
            props.pb_pick_mix_variant_title ||
            '',
          // In stuks, zoals de checkout ze toont: een multipack-item van de Pick &
          // Mix pagina is `quantity` packs van `packSize` stuks. De pagina deelt
          // dit bij het hydrateren weer terug in packs (packSize reist mee).
          quantity: Math.max(1, Number(item.quantity) || 1) * Math.max(1, Number(item.packSize) || 1),
          packSize: Math.max(1, Number(item.packSize) || 1),
          packLabel: item.packLabel || '',
          image: getPickMixImageUrl(item),
          url: item.url || '#',
          price: Number(item.price || 0),
          isGift: props._pick_mix_smyle_gift === 'true',
          // Stuks die gratis zijn door een actie binnen de bundel (vandaag: een
          // tandpasta bij een elektrische tandenborstel). Alleen voor het label
          // in de drawer; het bedrag komt van de kortingsfunctie.
          freeQty: Math.max(0, Number(props._pick_mix_free_qty || 0))
        };
      });

      window.PB_PICK_MIX_DISPLAY_BY_ID = window.PB_PICK_MIX_DISPLAY_BY_ID || {};
      window.PB_LAST_PICK_MIX_DISPLAY = pickMixDisplayItems;

      if (pickMixBundleId) {
        window.PB_PICK_MIX_DISPLAY_BY_ID[pickMixBundleId] = pickMixDisplayItems;
      }

      try {
        localStorage.setItem('pb_pick_mix_display_latest', JSON.stringify(pickMixDisplayItems));
        localStorage.setItem('pb_pick_mix_edit_url_latest', pickMixEditUrl);

        const pickMixChrome = {
          title: payload.bundleTitle || firstPickMixProps._pick_mix_title || '',
          image: payload.bundleImage || firstPickMixProps._pick_mix_image || ''
        };

        if (pickMixChrome.title || pickMixChrome.image) {
          localStorage.setItem('pb_pick_mix_chrome_latest', JSON.stringify(pickMixChrome));
        }

        if (pickMixBundleId) {
          localStorage.setItem(
            `pb_pick_mix_display_${pickMixBundleId}`,
            JSON.stringify(pickMixDisplayItems)
          );

          localStorage.setItem(
            `pb_pick_mix_edit_url_${pickMixBundleId}`,
            pickMixEditUrl
          );

          if (pickMixChrome.title || pickMixChrome.image) {
            localStorage.setItem(
              `pb_pick_mix_chrome_${pickMixBundleId}`,
              JSON.stringify(pickMixChrome)
            );
          }
        }
      } catch (e) {
        console.warn('[PBCart] Could not save Pick & Mix display data', e);
      }

      let cart = _cartPromise ? await _cartPromise : await cartQuery(_cartId);

      const existingPickMixLineIds = (cart?.lines?.edges || [])
        .map((edge) => edge.node)
        .filter((line) => {
          const attrs = line.attributes || [];
          const productTitle = String(line?.merchandise?.product?.title || '').toLowerCase();

          return (
            productTitle.includes('pick & mix') ||
            attrs.some((attr) => {
              return (
                (attr.key === '_pick_mix' || attr.key === 'pb_pick_mix') &&
                attr.value === 'true'
              );
            })
          );
        })
        .map((line) => line.id);

      if (existingPickMixLineIds.length) {
        window.PBDebug?.log('[PBCart] Removing existing Pick & Mix lines', existingPickMixLineIds);
        cart = await cartLinesRemove(_cartId, existingPickMixLineIds);
      }

      const lines = cartItems.map((item) => {
        const properties = item.properties || {};

        const attributes = Object.entries(properties)
          .filter(([key, value]) => key && value !== undefined && value !== null)
          .map(([key, value]) => {
            return {
              key,
              value: String(value)
            };
          });

        return {
          merchandiseId: gidFromVariant(item.id || item.variantId),
          quantity: Math.max(1, Number(item.quantity) || 1),
          attributes
        };
      });

      window.PBDebug?.log('[PBCart] Adding Pick & Mix lines', lines);

      cart = await cartLinesAdd(_cartId, lines);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);

      document.dispatchEvent(updatedEvent(cart, 'pickMixReplace'));
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { source: 'after_add' } }));

      return cart;
    },

    async replaceMarathonBundle(payload = {}) {
      await ensureCart();

      const cartItems = Array.isArray(payload.cartItems) ? payload.cartItems : [];

      if (!cartItems.length) {
        throw new Error('[PBCart] Marathon bundle payload has no cartItems');
      }

      let cart = _cartPromise ? await _cartPromise : await cartQuery(_cartId);

      // Only replace lines for this bundle id when explicitly updating.
      // New adds pass a fresh bundleId and leave other Marathon bundles intact.
      const bundleId = String(payload.bundleId || cartItems[0]?.properties?._marathon_bundle_id || '');
      const shouldReplaceExisting = Boolean(payload.replaceExisting && bundleId);

      if (shouldReplaceExisting) {
        const existingMarathonLineIds = (cart?.lines?.edges || [])
          .map((edge) => edge.node)
          .filter((line) => {
            const attrs = line.attributes || [];
            const isMarathon = attrs.some((attribute) => {
              return attribute.key === '_marathon_bundle' && attribute.value === 'true';
            });
            const matchesBundle = attrs.some((attribute) => {
              return attribute.key === '_marathon_bundle_id' && attribute.value === bundleId;
            });
            return isMarathon && matchesBundle;
          })
          .map((line) => line.id);

        if (existingMarathonLineIds.length) {
          cart = await cartLinesRemove(_cartId, existingMarathonLineIds);
        }
      }

      const lines = cartItems.map((item) => {
        const attributes = Object.entries(item.properties || {})
          .filter(([key, value]) => key && value !== undefined && value !== null)
          .map(([key, value]) => ({
            key,
            value: String(value)
          }));

        return {
          merchandiseId: gidFromVariant(item.id || item.variantId),
          quantity: Math.max(1, Number(item.quantity) || 1),
          attributes
        };
      });

      cart = await cartLinesAdd(_cartId, lines);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);

      document.dispatchEvent(updatedEvent(cart, 'marathonBundleReplace'));
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { source: 'after_add' } }));

      return cart;
    },

    /**
     * Replace any existing Mix & Match colour lines for this dummy product,
     * then add the chosen colour variants (Pick & Mix style). Never stacks.
     */
    async replaceMixMatchBundle(payload = {}) {
      await ensureCart();

      const productId = String(payload.productId || '').replace(/\D/g, '');
      const cartItems = Array.isArray(payload.cartItems) ? payload.cartItems : [];

      if (!productId || !cartItems.length) {
        throw new Error('[PBCart] Mix & Match payload requires productId and cartItems');
      }

      let cart = _cartPromise ? await _cartPromise : await cartQuery(_cartId);
      const existingLineIds = (cart?.lines?.edges || [])
        .map((edge) => edge.node)
        .filter((line) => {
          const attrs = line.attributes || [];
          const isMixMatch = attrs.some((attribute) => {
            return attribute.key === '_powr_mixmatch' && attribute.value === 'true';
          });
          const matchesProduct = attrs.some((attribute) => {
            return attribute.key === '_powr_mixmatch_product_id' && String(attribute.value) === productId;
          });
          return isMixMatch && matchesProduct;
        })
        .map((line) => line.id);

      if (existingLineIds.length) {
        cart = await cartLinesRemove(_cartId, existingLineIds);
      }

      const lines = cartItems.map((item) => {
        const attributes = Object.entries(item.properties || {})
          .filter(([key, value]) => key && value !== undefined && value !== null)
          .map(([key, value]) => ({
            key,
            value: String(value),
          }));

        return {
          merchandiseId: gidFromVariant(item.id || item.variantId),
          quantity: Math.max(1, Number(item.quantity) || 1),
          attributes,
        };
      });

      cart = await cartLinesAdd(_cartId, lines);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);

      document.dispatchEvent(updatedEvent(cart, 'mixMatchReplace'));
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { source: 'after_add' } }));

      return cart;
    },

    // A cart of its own, straight to its own checkout — NOT the shopper's cart.
    //
    // Pre-sale ships on its own schedule, so a pre-sale order may not carry
    // anything else. Every other method here works on the one shared cart in
    // the cookie; this one deliberately does not touch _cartId, _cartPromise or
    // the cookie, so whatever the shopper already had stays where it was and
    // the order can only hold what is passed in.
    //
    // Everything goes in one cartCreate: lines, attributes and the buyer, so
    // there is no second mutation that could half-succeed on a cart nobody
    // holds a reference to. Attribution is stamped here as well — a detached
    // cart never passes ensureAttributionAttributes, and without it the order
    // loses its first- and last-touch brand, its affiliate and its Dalton
    // assignment. Any attribution key added to ensureAttributionAttributes has
    // to be added here too.
    //
    // Unlike cartCreate() above, this reads userErrors: a variant that is not
    // published to the Cart Sync channel comes back as cart null with "Het
    // artikel met ID … bestaat niet", and silence there is a dead end.
    async createDetachedCheckout(payload = {}) {
      const items = Array.isArray(payload.items) ? payload.items : [];
      if (!items.length) throw new Error('[PBCart] Detached checkout has no items');

      const lines = items.map((item) => ({
        merchandiseId: gidFromVariant(item.id || item.variantId),
        quantity: Math.max(1, Number(item.quantity) || 1),
        attributes: Object.entries(item.properties || {})
          .filter(([key, value]) => key && value !== undefined && value !== null)
          .map(([key, value]) => ({ key, value: String(value) }))
      }));

      const affiliate = getAffiliateName();

      // Geen bestaande cart om mee samen te voegen: deze wordt hier gemaakt en
      // gaat rechtstreeks naar de kassa. Dus alleen wat de bezoeker nu heeft.
      const daltonAttribution = serializeDaltonAttribution(
        mergeDaltonAssignments(getDaltonStore())
      );
      const freeGiftTest = getFreeGiftTest();

      const attributes = [
        ...Object.entries(payload.attributes || {})
          .filter(([key, value]) => key && value !== undefined && value !== null)
          .map(([key, value]) => ({ key, value: String(value) })),
        { key: FIRST_TOUCH_KEY, value: String(getFirstTouchBrand()) },
        { key: LAST_TOUCH_KEY, value: String(getLastTouchValue()) },
        ...(affiliate ? [{ key: AFFILIATE_KEY, value: affiliate }] : []),
        ...(daltonAttribution ? [{ key: DALTON_KEY, value: daltonAttribution }] : []),
        ...(freeGiftTest ? [{ key: FREE_GIFT_TEST_KEY, value: freeGiftTest }] : [])
      ];

      const email = CUSTOMER_EMAIL();
      const codes = (Array.isArray(payload.discountCodes) ? payload.discountCodes : [])
        .map((code) => String(code || '').trim())
        .filter(Boolean);

      const input = {
        lines,
        attributes,
        ...(email ? { buyerIdentity: { email } } : {}),
        ...(codes.length ? { discountCodes: codes } : {})
      };

      window.PBDebug?.log('[PBCart] Detached checkout', input);

      const d = await gql(`
        mutation($input:CartInput!) {
          cartCreate(input:$input) {
            cart {
              id
              checkoutUrl
              totalQuantity
              lines(first: 50) { nodes { id quantity } }
            }
            warnings { code message target }
            userErrors { field message }
          }
        }
      `, { input });

      const result = d.cartCreate || {};
      const errors = result.userErrors || [];
      if (errors.length) {
        const refused = new Error('[PBCart] Detached checkout refused: ' + JSON.stringify(errors));
        refused.code = 'REFUSED';
        throw refused;
      }
      if (!result.cart?.checkoutUrl) {
        throw new Error('[PBCart] Detached checkout returned no checkout URL');
      }

      // A line whose variant cannot be sold in the asked quantity is clamped
      // rather than refused, so the cart can come back short without an error
      // of its own. The warnings are Shopify saying exactly which line that
      // was, and they are the only usable signal: THE QUANTITIES CANNOT BE
      // COMPARED, because a cart transform merges a bundle into one line of
      // quantity 1 — a cart of six items legitimately reports totalQuantity 1.
      const warnings = result.warnings || [];
      const short = warnings.filter((warning) =>
        /OUT_OF_STOCK|NOT_ENOUGH_STOCK/.test(String(warning.code || ''))
      );
      if (short.length) {
        const shortage = new Error('[PBCart] Detached checkout is short: ' + JSON.stringify(short));
        shortage.code = 'STOCK';
        throw shortage;
      }
      if (warnings.length) console.warn('[PBCart][detached]', warnings);

      if (!(result.cart.lines?.nodes || []).length) {
        throw new Error('[PBCart] Detached checkout came back without lines');
      }

      // TWO WAYS TO THE SAME CHECKOUT, and they are not interchangeable.
      // `checkoutUrl` is what the Storefront API hands back, and it is a CART
      // permalink: /cart/c/<token>?key=<key>. Opening it loads that cart into
      // the visitor's session and then forwards to the checkout — fine for the
      // tap on the page, which is a navigation happening right now.
      //
      // It is the wrong link to hand to an email. The `key` query parameter is
      // what a click tracker rewrites, re-encodes or truncates, and a key that
      // no longer matches is a 404 instead of a checkout; the route is also the
      // cart-restore route, so it puts these pre-sale lines in the shopper's
      // cart on the way through — the one thing this whole method exists to
      // avoid. `directCheckoutUrl` addresses the same cart as its checkout,
      // /checkouts/cn/<token>, with nothing in the query string to lose. That
      // is the form Shopify's own abandoned-checkout links take, and it is what
      // belongs in a Klaviyo flow.
      return {
        cart: result.cart,
        checkoutUrl: result.cart.checkoutUrl,
        directCheckoutUrl: directCheckoutUrl(result.cart.id, result.cart.checkoutUrl),
        warnings
      };
    },

    async removeLine(lineId) {
      await ensureCart();

      let cart = await cartLinesRemove(_cartId, [lineId]);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "remove"));
      return cart;
    },
    async removeLines(lineIds = []) {
      await ensureCart();

      const cleanLineIds = [...new Set(lineIds.filter(Boolean))];

      if (!cleanLineIds.length) {
        return _cartPromise || this.refresh();
      }

      let cart = await cartLinesRemove(_cartId, cleanLineIds);
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "remove"));

      return cart;
    },

    async setAttributes(obj = {}) {
      await ensureCart();

      const existingCart = _cartPromise ? await _cartPromise : null;
      const existingAttrs = existingCart?.attributes || [];

      // Merge new attributes with existing ones (including attribution keys)
      const newEntries = Object.entries(obj).map(([k, v]) => ({ key: k, value: String(v) }));
      const newKeys = new Set(newEntries.map(a => a.key));

      const preserved = existingAttrs
        .filter(a => !newKeys.has(a.key))
        .map(a => ({ key: a.key, value: a.value }));

      const attrs = [...preserved, ...newEntries];

      let cart = await cartAttributesUpdate(_cartId, attrs);
      // After a manual setAttributes, re-check attribution in case keys were overwritten
      cart = await ensureAttributionAttributes(cart);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, "attributes"));
      return cart;
    },

    // Zet een kortingscode op de cart zonder dat de klant iets intypt — voor
    // add-to-cart knoppen die hun eigen aanbieding meedragen. Voor de klant
    // voelt dat als een automatische korting; hij geldt alleen via die knoppen.
    //
    // Codes die er al op staan blijven staan. cartDiscountCodesUpdate vervangt
    // de lijst, en een code waarvan de drempel nog niet gehaald is komt terug
    // als applicable:false — die eruit filteren zou een korting weggooien die
    // straks alsnog telt.
    //
    // Een code die niet bestaat laat Shopify stilletjes vallen, dus de
    // teruggegeven cart is de enige plek waar te zien is of hij geland is.
    async applyDiscountCode(code) {
      const wanted = String(code || '').trim();
      if (!wanted) return this.current();

      await ensureCart();

      const existingCart = (_cartPromise ? await _cartPromise : null) || await this.refresh();
      const existing = (existingCart?.discountCodes || [])
        .map((entry) => String(entry.code || '').trim())
        .filter(Boolean);

      const already = existing.some((c) => c.toLowerCase() === wanted.toLowerCase());
      if (already) return existingCart;

      const cart = await cartDiscountCodesUpdate(_cartId, [...existing, wanted]);

      const landed = (cart?.discountCodes || [])
        .some((entry) => String(entry.code || '').toLowerCase() === wanted.toLowerCase());
      if (!landed) console.warn('[PBCart] discount code not accepted:', wanted);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, 'discount'));
      return cart;
    },

    // Haalt één code van de cart en laat de rest staan.
    async removeDiscountCode(code) {
      const unwanted = String(code || '').trim().toLowerCase();
      if (!unwanted) return this.current();

      await ensureCart();

      const existingCart = (_cartPromise ? await _cartPromise : null) || await this.refresh();
      const remaining = (existingCart?.discountCodes || [])
        .map((entry) => String(entry.code || '').trim())
        .filter(Boolean)
        .filter((c) => c.toLowerCase() !== unwanted);

      if (remaining.length === (existingCart?.discountCodes || []).length) return existingCart;

      const cart = await cartDiscountCodesUpdate(_cartId, remaining);

      _cartPromise = Promise.resolve(cart);
      document.dispatchEvent(updatedEvent(cart, 'discount'));
      return cart;
    },

    current() {
      return _cartPromise || this.refresh();
    },

    // Re-attach the logged-in shopper to the cart. Boot and refresh already do this;
    // call it directly after a login/logout that does not reload the page.
    async syncBuyerIdentity() {
      await ensureCart();
      return ensureBuyerIdentity(await this.current());
    },

    getCartId() {
      return _cartId || getCookie(CART_COOKIE);
    },

    setCartId(id) {
      _cartId = id || null;
      if (_cartId) setCookie(CART_COOKIE, _cartId);
      else delCookie(CART_COOKIE);
      _cartPromise = _cartId ? cartQuery(_cartId) : null;
      return _cartId;
    },

    async checkoutUrl() {
      let c = await this.refresh();
      c = await ensureAttributionAttributes(c);
      return c?.checkoutUrl || "";
    },

    async clear() {
      delCookie(CART_COOKIE);
      _cartId = null;
      _cartPromise = null;
      const c = await this.refresh();
      return c;
    }
  };

  window.PBCart = PBCart;

  // ---------- AUTO HOOK ----------
  if (AUTO_HOOK()) {
    document.addEventListener(
      "submit",
      async (e) => {
        const form = e.target?.closest('form[action*="/cart/add"]');
        if (!form) return;

        const idInput = form.querySelector('input[name="id"]');
        const qtyInput = form.querySelector('input[name="quantity"]');

        const variant = idInput?.value;
        const qty = parseInt(qtyInput?.value || "1", 10);

        const sellingPlanId = form.querySelector('[data-selling-plan-id]')?.dataset?.sellingPlanId || null;

        if (!variant) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        try {
          await PBCart.addLine(variant, qty, [], sellingPlanId);
        } catch (err) {
          console.error('[PBCart] autoHook /cart/add failed', err);
        }
      },
      { capture: true }
    );
  }

  // ---------- INIT ----------
  (async () => {
    try {
      await ensureCart();
    } catch (e) {
      console.error("[PBCart][boot]", e);
    }
  })();

  // ---------- WONDR 5 JAAR — PRIZE DRAW ----------
  const WONDR_PRIZE_ATTRIBUTE = 'wondr_prize_number';

  const WONDR_PRIZE_PROBABILITIES = [
    { number: 1, weight: 0.125 }, // Kids bar apple splash
    { number: 2, weight: 0.125 }, // All-in-1 blossom bliss
    { number: 3, weight: 0.125 }, // Kaarshouder crème + Cosy spice refill
    { number: 4, weight: 0.125 }, // Scrub reviving mocha
    { number: 5, weight: 0.125 }, // Scalp massager wit
    { number: 6, weight: 0.125 }, // Travel case Yellow
    { number: 7, weight: 0.125 }, // Conditioner lavender haze
    { number: 8, weight: 0.125 }, // Clay detox mask
  ];

  function wondrDrawPrize(probabilities) {
    const rand = Math.random();
    let cumulative = 0;
    for (const prize of probabilities) {
      cumulative += prize.weight;
      if (rand < cumulative) return prize.number;
    }
    return null;
  }

  async function wondrMaybeDrawPrize() {
    try {
      // 🔒 Campagne guard — enkel actief in test mode of wanneer campagne live is
      const isTestMode = new URLSearchParams(location.search).get('wondr_test') === '1';
      const isCampaignLive = false; // ← zet op false op 30/03

      if (!isTestMode && !isCampaignLive) {
        window.PBDebug?.log('[Wondr] Prize draw: inactief');
        return null;
      }

      const cart = await PBCart.current();
      const lines = (cart?.lines?.edges || []).map(e => e.node);

      const eligibleQty = lines
        .filter(line => !line.attributes?.some(a => a.key === '_auto_gift' && a.value === 'true'))
        .reduce((sum, line) => sum + line.quantity, 0);

      window.PBDebug?.log('[Wondr] Eligible qty:', eligibleQty);

      if (eligibleQty < 4) {
        window.PBDebug?.log('[Wondr] Prize draw: not eligible');
        return null;
      }

      const existing = cart.attributes?.find(a => a.key === WONDR_PRIZE_ATTRIBUTE);
      if (existing?.value) {
        window.PBDebug?.log('[Wondr] Prize already drawn:', existing.value);
        return existing.value;
      }

      const prizeNumber = wondrDrawPrize(WONDR_PRIZE_PROBABILITIES);
      if (prizeNumber) {
        await PBCart.setAttributes({ [WONDR_PRIZE_ATTRIBUTE]: String(prizeNumber) });
        window.PBDebug?.log('[Wondr] Prize drawn:', prizeNumber);
        return prizeNumber;
      }
    } catch (e) {
      console.warn('[Wondr] Prize draw failed', e);
    }
    return null;
  }

  window.wondrMaybeDrawPrize = wondrMaybeDrawPrize;

})();